import React from "react";
import { useTranslation } from "react-i18next";
import { buildSeasonOptions } from "../../utils/seasons";

export type SizeOption = "XS" | "S" | "M" | "L" | "XL" | "XXL";

export type SortValue =
  | "relevance"
  | "price-asc"
  | "price-desc"
  | "season-desc"
  | "season-asc"
  | "views-desc"
  | "newest";

interface SortOption {
  value: SortValue;
  label: string;
}

export type LeagueOption = {
  id: string;
  label: string;
  color: string;
  abbr: string;
};

export interface FilterSidebarProps {
  resultsCount?: number;
  leagueOptions?: LeagueOption[];
  selectedLeagues?: Set<string>;
  onToggleLeague?: (leagueId: string) => void;
  sizeOptions?: SizeOption[];
  selectedSizes?: Set<SizeOption>;
  onToggleSize?: (size: SizeOption) => void;
  seasonOptions?: string[];
  selectedSeason?: string;
  onSeason?: (season: string) => void;
  priceMin?: string;
  priceMax?: string;
  onPriceMin?: (value: string) => void;
  onPriceMax?: (value: string) => void;
  sortOptions?: SortOption[];
  sortBy?: SortValue;
  onSortBy?: (value: SortValue) => void;
  onApply?: () => void;
  onClear?: () => void;
}

const FSM_GREEN = "#237061";
const FSM_GREEN_LIGHT = "#4E9E84";

export default function FilterSidebar({
  resultsCount = 0,
  leagueOptions = [],
  selectedLeagues = new Set<string>(),
  onToggleLeague = () => {},
  sizeOptions = ["S", "M", "L", "XL", "XXL"],
  selectedSizes = new Set<SizeOption>(),
  onToggleSize = () => {},
  seasonOptions = buildSeasonOptions(1980),
  selectedSeason = "",
  onSeason = () => {},
  priceMin = "",
  priceMax = "",
  onPriceMin = () => {},
  onPriceMax = () => {},
  sortOptions = [
    { value: "relevance", label: "Relevantie" },
    { value: "price-asc", label: "Prijs oplopend" },
    { value: "price-desc", label: "Prijs aflopend" },
    { value: "newest", label: "Nieuwste eerst" },
    { value: "season-desc", label: "Seizoen ↓" },
    { value: "season-asc", label: "Seizoen ↑" },
    { value: "views-desc", label: "Meest bekeken" },
  ],
  sortBy = "relevance",
  onSortBy = () => {},
  onApply = () => {},
  onClear = () => {},
}: FilterSidebarProps) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        background: "#ffffff",
        border: `2px solid ${FSM_GREEN}`,
        borderRadius: 16,
        width: "100%",
        overflow: "hidden",
        boxShadow: `0 6px 32px rgba(35,112,97,0.13)`,
        fontFamily: "'Open Sans', sans-serif",
      }}
    >
      <div
        style={{
          background: FSM_GREEN,
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              color: "#fff",
              fontWeight: 800,
              fontSize: 16,
              letterSpacing: 1,
              textTransform: "uppercase",
            }}
          >
            Filters
          </span>
        </div>

        <span
          style={{
            background: "rgba(255,255,255,0.18)",
            color: "#fff",
            borderRadius: 99,
            fontSize: 12,
            fontWeight: 700,
            padding: "3px 12px",
            letterSpacing: 0.5,
          }}
          aria-live="polite"
        >
          {resultsCount} resultaten
        </span>
      </div>

      <div
        style={{
          height: 3,
          background: `linear-gradient(90deg, ${FSM_GREEN} 0%, #2FAF8A 50%, ${FSM_GREEN_LIGHT} 100%)`,
        }}
      />

      <div style={{ padding: 20 }}>
        <FilterSection label={t("catalog.competition")}>
          <select
            value={Array.from(selectedLeagues)[0] ?? ""}
            onChange={(e) => {
              const val = e.target.value;
              Array.from(selectedLeagues).forEach((id) => onToggleLeague(id));
              if (val) onToggleLeague(val);
            }}
            style={selectStyle}
            aria-label={t("catalog.competition")}
          >
            <option value="">{t("catalog.allCompetitions")}</option>
            {leagueOptions.map(({ id, label }) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </FilterSection>

        <FilterDivider />

        <FilterSection label="Maat">
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 4,
            }}
            role="group"
            aria-label="Maat"
          >
            {sizeOptions.map((s) => {
              const active = selectedSizes.has(s);
              return (
                <button
                  key={s}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onToggleSize(s)}
                  style={{
                    border: active
                      ? `2px solid ${FSM_GREEN}`
                      : "2px solid #dce8e3",
                    borderRadius: 8,
                    background: active ? FSM_GREEN : "#fff",
                    color: active ? "#fff" : "#333",
                    fontWeight: 800,
                    fontSize: 13,
                    width: 46,
                    height: 40,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    outline: "none",
                    letterSpacing: 0.5,
                    fontFamily: "inherit",
                  }}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </FilterSection>

        <FilterDivider />

        <FilterSection label="Seizoen">
          <select
            value={selectedSeason}
            onChange={(e) => onSeason(e.target.value)}
            style={selectStyle}
          >
            <option value="">Alle seizoenen</option>
            {seasonOptions.map((season) => (
              <option key={season} value={season}>
                {season}
              </option>
            ))}
            <option value="pre-1980">Pre 1980</option>
          </select>
        </FilterSection>

        <FilterDivider />

        <FilterSection label="Prijs (€)">
          <div style={{ display: "flex", gap: 10, width: "100%" }}>
            <input
              type="number"
              inputMode="numeric"
              placeholder="Min"
              value={priceMin}
              onChange={(e) => onPriceMin(e.target.value)}
              style={{ ...inputStyle, flex: 1, minWidth: 0 }}
            />
            <input
              type="number"
              inputMode="numeric"
              placeholder="Max"
              value={priceMax}
              onChange={(e) => onPriceMax(e.target.value)}
              style={{ ...inputStyle, flex: 1, minWidth: 0 }}
            />
          </div>
        </FilterSection>

        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button
            type="button"
            onClick={onClear}
            style={{
              flex: 1,
              padding: "11px 0",
              border: `2px solid ${FSM_GREEN}`,
              borderRadius: 10,
              background: "#fff",
              color: FSM_GREEN,
              fontWeight: 800,
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "inherit",
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            Wissen
          </button>

          <button
            type="button"
            onClick={onApply}
            style={{
              flex: 2,
              padding: "11px 0",
              border: "none",
              borderRadius: 10,
              background: FSM_GREEN,
              color: "#fff",
              fontWeight: 800,
              fontSize: 13,
              cursor: "pointer",
              fontFamily: "inherit",
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            Toepassen
          </button>
        </div>

        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: "1px solid #e8f0ec",
            textAlign: "center",
            fontSize: 10,
            color: FSM_GREEN_LIGHT,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: "uppercase",
          }}
        >
          FSM · Football Shirt Market
        </div>
      </div>
    </div>
  );
}

function FilterSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: FSM_GREEN,
          textTransform: "uppercase",
          letterSpacing: 1.2,
          marginBottom: 10,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function FilterDivider() {
  return <div style={{ height: 1, background: "#e8f0ec", margin: "16px 0" }} />;
}

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 14px",
  border: "2px solid #dce8e3",
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 600,
  color: "#1a1a1a",
  background: "#fafcfb",
  appearance: "none",
  cursor: "pointer",
  fontFamily: "inherit",
  outline: "none",
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  padding: "10px 12px",
  border: "2px solid #dce8e3",
  borderRadius: 10,
  fontSize: 13,
  color: "#1a1a1a",
  background: "#fafcfb",
  fontFamily: "inherit",
  outline: "none",
};